import React, { useEffect, useContext } from 'react';
import { AxiosError } from 'axios';
import { NavLink, Outlet } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import { EVENTS } from 'constants/events';
import { logEvent } from 'utils/amplitude';

import styled from 'styled-components';
import { COLORS } from '@joinforma/ui/constants/legacy/theme';
import Headline2 from '@joinforma/ui/components/Headline2';
import Title2 from '@joinforma/ui/components/Title2';
import { ReactComponent as PlusCircleIcon } from '@joinforma/ui/assets/icons/legacy/plus-circle.svg';

import useQueryStripeCards from 'hooks/useQueryStripeCards';

import { CARD_STATUSES } from 'constants/stripe';
import CurrentUserContext from 'contexts/CurrentUserContext';
import { SettingsRoutes } from 'utils/routeGenerator';

import { Employee, PosttaxCard } from './types';

const Container = styled.div``;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AddCardLink = styled(NavLink)`
  outline: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  cursor: pointer;
`;

const AddIcon = styled(PlusCircleIcon)`
  width: 20px;
  height: 20px;
  margin-right: 5px;
  path {
    stroke: ${COLORS.OCEAN};
  }
`;

const AddCardLinkText = styled(Title2)`
  color: ${COLORS.OCEAN};
`;

const Title = styled(Headline2)`
  margin-bottom: 24px;
`;

const NavigationBar = styled.div`
  border-bottom: 1px solid ${COLORS.GREY_100};
`;

const NavigationLinkText = styled(Title2)`
  padding: 13px 8px;
  display: inline-block;
`;

const NavigationLink = styled(NavLink).attrs({ end: true })`
  display: inline-block;
  ${NavigationLinkText} {
    color: ${COLORS.GREY_400};
  }
  :hover {
    border-bottom: 3px solid ${COLORS.BLACK_50};
    ${NavigationLinkText} {
      color: ${COLORS.BLACK_50};
    }
  }
  &.active {
    border-bottom: 3px solid ${COLORS.BLACK_50};
    ${NavigationLinkText} {
      color: ${COLORS.BLACK_50};
    }
  }
`;

const Main = styled.div`
  padding-top: 64px;
`;

const CardsLayout = ({ children }: { children?: React.ReactNode }) => {
  const { employee }: { employee: Employee } = useContext(CurrentUserContext);
  const handleError = useErrorHandler();
  const { data: cards_data, is_loading: is_cards_loading } =
    useQueryStripeCards({
      onError: (error: AxiosError) => handleError(error),
    });

  useEffect(() => {
    logEvent(EVENTS.TwicCardViewed, {});
  }, []);

  const renderButton = (cards: Array<PosttaxCard>) => {
    const active_cards = cards.filter(
      ({ status }) => status !== CARD_STATUSES.CANCELED
    );
    const has_physical_card = active_cards.some(
      ({ type }) => type === 'physical'
    );
    const has_virtual_card = active_cards.some(
      ({ type }) => type === 'virtual'
    );

    const has_at_least_one_card = has_physical_card || has_virtual_card;
    const has_both_cards = has_physical_card && has_virtual_card;

    if (has_at_least_one_card && !has_both_cards) {
      return (
        <AddCardLink
          to={SettingsRoutes.posttaxCardNew({
            card_type: has_physical_card ? 'virtual' : 'physical',
          })}
        >
          <AddIcon />
          <AddCardLinkText>
            Add {has_physical_card ? 'Virtual' : 'Debit'} Card
          </AddCardLinkText>
        </AddCardLink>
      );
    }

    return null;
  };

  const renderCardSettingsLink = (employee: Employee) => {
    const {
      settings: { country, stripe_cardholder_exists },
    } = employee;

    if (country === 'us' && stripe_cardholder_exists) {
      return (
        <NavigationLink to={SettingsRoutes.posttaxCardEdit()}>
          <NavigationLinkText>Card settings</NavigationLinkText>
        </NavigationLink>
      );
    }

    return null;
  };
  return (
    <Container>
      <TitleContainer>
        <Title>Forma Card</Title>
        {!is_cards_loading && renderButton(cards_data!.cards)}
      </TitleContainer>
      <NavigationBar>
        <NavigationLink to={SettingsRoutes.posttaxCardList()}>
          <NavigationLinkText>Manage cards</NavigationLinkText>
        </NavigationLink>
        {renderCardSettingsLink(employee)}
      </NavigationBar>
      <Main>{children ?? <Outlet />}</Main>
    </Container>
  );
};

export default CardsLayout;
